import {
  createContext,
  ReactChild,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import Phaser from 'phaser'

import { Arena, ArenaEvents, events } from 'scenes'
import useMount from 'hooks/useMount'
import { ArenaGladiator } from 'scenes/Arena'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const debug = require('debug')('app:context:GameEngine')

interface GameActions {
  fight: () => Promise<void>
  announceWinner: (winner: ArenaGladiator.Left|ArenaGladiator.Right) => void
  announceGladiator1Win: () => Promise<void>
  announceGladiator2Win: () => Promise<void>
  announceDraw: () => Promise<void>
  reset: () => Promise<void>
}

interface GameContext {
  actions: GameActions
  selected: ArenaGladiator
}

async function empty() {}
const emptyActions = {
  fight: empty,
  announceWinner: empty,
  announceGladiator1Win: empty,
  announceGladiator2Win: empty,
  announceDraw: empty,
  reset: empty,
}

const waitAnimation = (ms: number) => new Promise((res) => {
  window.setTimeout(res, ms)
})

export const context = createContext<GameContext|null>(null)

export function Provider({ children }: { children: ReactChild }) {
  const game = useRef<Phaser.Game|null>(null)
  const arena = useRef<Arena|null>(null)

  const [selected, setSelected] = useState<ArenaGladiator>(ArenaGladiator.None)
  const [actions, setActions] = useState<GameActions>(emptyActions)
  const value = useMemo(() => ({
    actions,
    selected,
  }), [actions, selected])

  useMount(() => {
    game.current = new Phaser.Game({
      type: Phaser.AUTO,
      parent: 'arena',
      backgroundColor: '#E19B5C',
      scale: {
        mode: Phaser.Scale.ScaleModes.WIDTH_CONTROLS_HEIGHT,
        width: 512,
        height: 523,
      },
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 2000 },
        },
      },
      scene: [Arena],
    })

    events.on(ArenaEvents.SelectGladitor, (gladiator: 0|1) => {
      setSelected(gladiator)
    })

    events.on(ArenaEvents.Ready, (ar: Arena) => { arena.current = ar })

    setActions({
      announceWinner: async (winner) => {
        arena.current!.announceWinner(winner)
        await waitAnimation(2000)
      },
      fight: async () => {
        arena.current!.fight()
        await waitAnimation(1000)
      },
      announceGladiator1Win: async () => {
        arena.current!.announceWinner(ArenaGladiator.Left)
        await waitAnimation(1500)
      },
      announceGladiator2Win: async () => {
        arena.current!.announceWinner(ArenaGladiator.Right)
        await waitAnimation(1500)
      },
      announceDraw: async () => {
        arena.current!.announceWinner(ArenaGladiator.None)
        await waitAnimation(1500)
      },
      reset: async () => {
        arena.current!.reset()
        await waitAnimation(1200)
      },
    })
  })

  return (
    <context.Provider value={value}>
      { children }
    </context.Provider>
  )
}

export const useGame = () => useContext<GameContext|null>(context)

export default useGame
